import { getUserId } from '../userLocalDetails';

export const MAINTENANCE_CODE = 'SUBMISSION_MAINTENANCE';
export const MAINTENANCE_EVENT = 'jeddle:submission-maintenance';
const pendingAnswers = new Map();
const acknowledged = new Map();
let session;
let sequence = 0;
let revision = 0;
export const beginSubmissionRequest = () => ({
  owner: currentSession(),
  sequence: ++sequence,
});
let state = { enabled: false, retryAfterSeconds: 600, message: '' };

const currentSession = () => {
  const next = getUserId?.() || 'anonymous';
  if (next !== session) {
    pendingAnswers.clear();
    acknowledged.clear();
    session = next;
  }
  return next;
};
const emit = () => window.dispatchEvent(new Event(MAINTENANCE_EVENT));

export const maintenanceMessage = (seconds = 600) =>
  `We're updating Jeddle. Please try again in about ${Math.ceil(
    seconds / 60
  )} minutes. You can keep writing in this tab, but response changes cannot be saved or submitted yet. Keep this tab open or copy your work. The update may take longer.`;

export function getSubmissionMaintenance() {
  currentSession();
  return { ...state, revision, unsavedAnswers: pendingAnswers.size };
}

export function updateSubmissionMaintenance(next, expectedRevision) {
  if (expectedRevision !== undefined && expectedRevision !== revision) return;
  revision++;
  currentSession();
  const seconds = Number(next.retryAfterSeconds);
  state = {
    enabled: next.enabled === true,
    retryAfterSeconds: seconds >= 60 && seconds <= 86400 ? seconds : 600,
  };
  state.message = state.enabled
    ? maintenanceMessage(state.retryAfterSeconds)
    : '';
  // Resumed status is NOT an acknowledgement that unsaved writing was saved.
  emit();
}

const answerKey = (url) => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    return /^\/submissions\/[^/]+\/answers\/[^/]+$/.test(path) ? path : null;
  } catch {
    return null;
  }
};

/** Only rejected answer snapshots are retained, in memory and scoped to this signed-in user. */
export async function checkSubmissionMaintenance(
  response,
  url,
  options = {},
  ticket = beginSubmissionRequest()
) {
  const owner = ticket.owner;
  if (response.status !== 503) return;
  let body;
  try {
    body = await response.clone().json();
  } catch {
    return;
  }
  if (body?.code !== MAINTENANCE_CODE) return;
  const key = answerKey(url);
  if (
    currentSession() === owner &&
    key &&
    options.method === 'PATCH' &&
    typeof options.body === 'string' &&
    Math.max(
      pendingAnswers.get(key)?.sequence ?? -1,
      acknowledged.get(key) ?? -1
    ) < ticket.sequence
  ) {
    try {
      pendingAnswers.set(key, {
        owner,
        sequence: ticket.sequence,
        body: JSON.parse(options.body),
      });
    } catch {
      /* No snapshot to recover. */
    }
  }
  updateSubmissionMaintenance({
    enabled: true,
    retryAfterSeconds: body.retryAfterSeconds,
  });
  throw Object.assign(new Error(state.message), {
    code: MAINTENANCE_CODE,
    status: 503,
    retryAfterSeconds: state.retryAfterSeconds,
    maintenanceHandled: true,
  });
}

export function acknowledgeMaintenanceAnswer(
  url,
  options = {},
  ticket = beginSubmissionRequest(),
  responseData
) {
  if (currentSession() !== ticket.owner) return;
  const key = answerKey(url);
  if (!key || options.method !== 'PATCH') return;
  if (
    responseData !== undefined &&
    !responseData?.answers?.some(
      (row) =>
        String(row.serialNumber) === decodeURIComponent(key.split('/').pop()) &&
        row.answer != null
    )
  )
    return;
  acknowledged.set(key, Math.max(acknowledged.get(key) ?? -1, ticket.sequence));
  const pending = pendingAnswers.get(key);
  // A successful older save must not erase a newer rejected snapshot.
  if (pending && pending.sequence < ticket.sequence) {
    pendingAnswers.delete(key);
    emit();
  }
}

export function maintenanceAnswerBackup() {
  currentSession();
  return [...pendingAnswers.entries()].map(([path, value]) => ({
    path,
    answer: value.body,
  }));
}

export const isSubmissionMaintenance = (error) =>
  error?.code === MAINTENANCE_CODE;
